import { useState, useEffect, useCallback } from "react";
import { Button, Modal } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";
import CouponCodeTable from "./CouponCodeTable";
import CouponCodeModal from "./CouponCodeModal";
import SettingsPageLayout from "../SettingsPageLayout";

const CouponCode = () => {
    const { t } = useTranslation("settings");
    const toast = useToast();
    const [couponCodes, setCouponCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [searchParams, setSearchParams] = useState({});
    const [isSearching, setIsSearching] = useState(false);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("create"); // 'create', 'update', 'view'
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    const fetchCouponCodes = useCallback(
        async (page = 1, pageSizeParam = 10, searchParameters = {}) => {
            try {
                setLoading(true);

                // Build query string with search parameters
                const queryParams = new URLSearchParams({
                    page: page.toString(),
                    limit: pageSizeParam.toString(),
                });

                // Add search parameters
                if (searchParameters.code) {
                    queryParams.append("code", searchParameters.code);
                }

                const response = await apiClient.get(`api/promo-code/list?${queryParams.toString()}`);

                if (response?.data?.success) {
                    const result = response?.data?.data?.result || [];
                    const meta = response?.data?.data?.meta || {};

                    setCouponCodes(
                        result.map((coupon, index) => ({
                            ...coupon,
                            key: coupon._id || index,
                        })),
                    );
                    setTotalItems(meta.total || 0);
                }
            } catch (_error) {
                toast.error({
                    message: t("couponCode.actionError"),
                    description: t("couponCode.fetchFailed"),
                });
                setCouponCodes([]);
                setTotalItems(0);
            } finally {
                setLoading(false);
            }
        },
        [t, toast],
    );

    // Handle search params changes
    useEffect(() => {
        const targetPage = isSearching ? 1 : currentPage;
        fetchCouponCodes(targetPage, pageSize, searchParams);

        if (isSearching) {
            setIsSearching(false);
            setCurrentPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, isSearching]);

    // Handle pagination changes
    useEffect(() => {
        fetchCouponCodes(currentPage, pageSize, searchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize]);

    // Handle code search from table column
    const handleCodeSearch = (codeValue) => {
        const newSearchParams = {};
        if (codeValue && codeValue.trim()) {
            newSearchParams.code = codeValue.trim();
        }
        setSearchParams(newSearchParams);
        setIsSearching(true);
    };

    // Handle create button click
    const handleCreate = () => {
        setSelectedCoupon(null);
        setModalMode("create");
        setModalOpen(true);
    };

    // Handle view action
    const handleView = (coupon) => {
        setSelectedCoupon(coupon);
        setModalMode("view");
        setModalOpen(true);
    };

    // Handle update action
    const handleUpdate = (coupon) => {
        setSelectedCoupon(coupon);
        setModalMode("update");
        setModalOpen(true);
    };

    // Handle delete action
    const handleDelete = (coupon) => {
        Modal.confirm({
            title: t("couponCode.deleteTitle"),
            content: t("couponCode.deleteConfirm", { code: coupon.code }),
            okText: t("delete"),
            okType: "danger",
            cancelText: t("cancel"),
            centered: true,
            okButtonProps: {
                shape: "round",
            },
            cancelButtonProps: {
                shape: "round",
            },
            onOk: async () => {
                try {
                    const response = await apiClient.delete(`/api/promo-code/delete/${coupon.code}`);
                    if (response?.data?.success) {
                        toast.success({
                            message: t("couponCode.actionSuccess"),
                            description: t("couponCode.deletedSuccess"),
                        });
                        // Refresh the table
                        fetchCouponCodes(currentPage, pageSize, searchParams);
                    } else {
                        toast.error({
                            message: t("couponCode.actionError"),
                            description: response?.data?.message || t("couponCode.deleteFailed"),
                        });
                    }
                } catch (error) {
                    toast.error({
                        message: t("couponCode.actionError"),
                        description: error?.response?.data?.message || t("couponCode.deleteFailed"),
                    });
                }
            },
        });
    };

    // Handle modal submit
    const handleModalSubmit = async (formData) => {
        try {
            setModalLoading(true);

            if (modalMode === "create") {
                const response = await apiClient.post("/api/promo-code/create", formData);
                if (response?.data?.success) {
                    toast.success({
                        message: t("couponCode.actionSuccess"),
                        description: t("couponCode.createdSuccess"),
                    });
                    setModalOpen(false);
                    fetchCouponCodes(currentPage, pageSize, searchParams);
                } else {
                    toast.error({
                        message: t("couponCode.actionError"),
                        description: response?.data?.message || t("couponCode.createFailed"),
                    });
                }
            } else if (modalMode === "update") {
                const response = await apiClient.patch(`/api/promo-code/update/${selectedCoupon._id}`, formData);
                if (response?.data?.success) {
                    toast.success({
                        message: t("couponCode.actionSuccess"),
                        description: t("couponCode.updatedSuccess"),
                    });
                    setModalOpen(false);
                    fetchCouponCodes(currentPage, pageSize, searchParams);
                } else {
                    toast.error({
                        message: t("couponCode.actionError"),
                        description: response?.data?.message || t("couponCode.updateFailed"),
                    });
                }
            }
        } catch (error) {
            toast.error({
                message: t("couponCode.actionError"),
                description: error?.response?.data?.message || t("couponCode.genericError"),
            });
        } finally {
            setModalLoading(false);
        }
    };

    // Handle modal cancel
    const handleModalCancel = () => {
        setModalOpen(false);
        setSelectedCoupon(null);
    };

    return (
        <SettingsPageLayout
            title={t("couponCode.title")}
            count={totalItems}
            description={t("couponCode.description")}
            actions={
                <Button
                    type="primary"
                    shape="round"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                    className="w-full sm:w-auto">
                    {t("couponCode.create")}
                </Button>
            }>
            <CouponCodeTable
                couponCodes={couponCodes}
                loading={loading}
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={totalItems}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                onView={handleView}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onCodeSearch={handleCodeSearch}
                searchCode={searchParams.code ?? ""}
            />

            <CouponCodeModal
                open={modalOpen}
                mode={modalMode}
                couponData={selectedCoupon}
                onCancel={handleModalCancel}
                onSubmit={handleModalSubmit}
                loading={modalLoading}
            />
        </SettingsPageLayout>
    );
};

CouponCode.propTypes = {
    companyData: PropTypes.shape({
        couponCodes: PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({
                    code: PropTypes.string,
                    discount: PropTypes.number,
                    status: PropTypes.string,
                }),
            ]),
        ),
    }),
    initialLoading: PropTypes.bool,
};

export default CouponCode;
